import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SparklesIcon, RocketIcon, LayersIcon, LayoutTemplateIcon, PaintbrushIcon, NavigationIcon, SearchIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ContactFormPopup } from './ContactFormPopup';
import { useMotionValue, useTransform, animate } from 'framer-motion';
const bulletIcons = [LayoutTemplateIcon, PaintbrushIcon, NavigationIcon, SearchIcon];
function AnimatedCounter({
  value,
  prefix = '',
  suffix = '',
  inView
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  inView: boolean;
}) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, latest => {
    return Math.round(latest).toString();
  });
  useEffect(() => {
    if (inView) {
      const controls = animate(count, value, {
        duration: 2.2,
        ease: [0.16, 1, 0.3, 1]
      });
      return controls.stop;
    }
  }, [inView, value]);
  return <span className="inline-flex items-baseline whitespace-pre-wrap" data-id="element-253">
      {prefix}
      <motion.span data-id="element-254">{rounded}</motion.span>
      {suffix}
    </span>;
}
export function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const cards = [{
    icon: SparklesIcon,
    titleKey: 'pricing.demo.title',
    descKey: 'pricing.demo.desc',
    bullets: ['pricing.demo.b1', 'pricing.demo.b2', 'pricing.demo.b3'],
    priceKey: 'pricing.demo.price',
    ctaKey: 'pricing.demo.cta',
    label: t('pricing.demo.label') || 'FREE'
  }, {
    icon: RocketIcon,
    titleKey: 'pricing.single.title',
    descKey: 'pricing.single.desc',
    bullets: ['pricing.single.b1', 'pricing.single.b2', 'pricing.single.b3', 'pricing.single.b4'],
    priceKey: 'pricing.single.price',
    ctaKey: 'pricing.single.cta'
  }, {
    icon: LayersIcon,
    titleKey: 'pricing.multi.title',
    descKey: 'pricing.multi.desc',
    bullets: ['pricing.multi.b1', 'pricing.multi.b2', 'pricing.multi.b3', 'pricing.multi.b4'],
    priceKey: 'pricing.multi.price',
    ctaKey: 'pricing.multi.cta'
  }];
  const parsePrice = (val: string) => {
    const match = val.match(/^([^\d]*)(\d+)(.*)$/);
    if (match) {
      return {
        prefix: match[1],
        value: parseInt(match[2], 10),
        suffix: match[3]
      };
    }
    return {
      prefix: val,
      value: 0,
      suffix: ''
    };
  };
  return <>
      <section id="services" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-255">
        <div className="relative z-10 mx-auto max-w-7xl" data-id="element-256">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="mb-12 sm:mb-16 text-center" data-id="element-257">
            <h2 className="text-3xl md:text-4xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-258">
              {t('pricing.title')} {t('pricing.titleHighlight')}
            </h2>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-3 max-w-7xl mx-auto" data-id="element-259">
            {cards.map((card, cardIndex) => <motion.div key={cardIndex} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.1 + cardIndex * 0.1
          }} data-id="element-260">
                <div className="h-full rounded-3xl border p-6 sm:p-8 flex flex-col relative" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-261">
                  {/* Label centered at top */}
                  {card.label && <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium tracking-wider uppercase" style={{
                backgroundColor: 'var(--accent)',
                color: '#fff'
              }} data-id="element-262">
                      {card.label}
                    </div>}

                  {/* Spacer — same height for all cards to keep content aligned */}
                  <div className="h-8" data-id="element-263" />

                  {/* Icon + Title row */}
                  <div className="flex items-center gap-3 mb-7" data-id="element-264">
                    <div className="p-2.5 rounded-xl flex-shrink-0" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-265">
                      <card.icon className="w-5 h-5" style={{
                    color: 'var(--accent)'
                  }} data-id="element-266" />
                    </div>
                    <h3 className="text-base sm:text-lg font-medium" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-267">
                      {t(card.titleKey)}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className="text-sm sm:text-base mb-8" style={{
                color: 'var(--text-secondary)',
                minHeight: '4em'
              }} data-id="element-268">
                    {t(card.descKey)}
                  </p>

                  {/* Bullet list — consistent height across cards */}
                  <ul className="space-y-3.5 mb-8 flex-1" style={{
                minHeight: '10rem'
              }} data-id="element-269">
                    {card.bullets.map((bulletKey, i) => {
                  const text = t(bulletKey);
                  if (!text || text === bulletKey) return null;
                  const Icon = bulletIcons[i] || bulletIcons[0];
                  return <li key={i} className="flex items-start gap-3" data-id="element-270">
                          <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{
                      backgroundColor: 'var(--bg-tertiary)'
                    }} data-id="element-271">
                            <Icon className="w-3.5 h-3.5" style={{
                        color: 'var(--accent)'
                      }} data-id="element-272" />
                          </div>
                          <span className="text-sm sm:text-base" style={{
                      color: 'var(--text-secondary)'
                    }} data-id="element-273">
                            {text}
                          </span>
                        </li>;
                })}
                  </ul>

                  {/* Price + CTA */}
                  <div className="mt-auto pt-6 border-t" style={{
                borderColor: 'var(--border)'
              }} data-id="element-274">
                    <div className="mb-5 text-center" data-id="element-275">
                      <span className="text-2xl sm:text-3xl font-medium" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-276">
                        {(() => {
                      const priceStr = card.price || t(card.priceKey!);
                      const parsed = parsePrice(priceStr);
                      if (parsed.value > 0 || priceStr.includes('0')) {
                        return <AnimatedCounter value={parsed.value} prefix={parsed.prefix} suffix={parsed.suffix} inView={isInView} data-id="element-277" />;
                      }
                      return priceStr;
                    })()}
                      </span>
                    </div>
                    <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-medium transition-all hover:scale-[1.02]" style={{
                  backgroundColor: 'var(--accent)',
                  color: '#FFFFFF'
                }} data-id="element-278">
                      {t(card.ctaKey)}
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-279" />
    </>;
}